import { Block, Flex, SizeProperty, Typography } from '@aloft/toolkit-sui';
// eslint-disable-next-line aloft/no-sui-chakra
import { Textarea } from '@aloft/toolkit-sui/chakra';

import { Fragment } from 'react';
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form';

export type FormTextareaProps<T extends FieldValues> = {
  name: FieldPath<T>;
  control: Control<T>;
  label: string;
  placeholder?: string;
  isRequired?: boolean;
  labelWidth?: string;
  column?: boolean;
  inputWidth?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  resize?: 'none' | 'horizontal' | 'vertical' | 'both';
  customErrorMessage?: string;
  onClear?: () => void;
  labelVariant?: 'h4' | 'h3' | 'label';
  labelColor?: 'textSecondary' | 'textTertiary' | 'textPrimary';
  height?: string;
};

export const FormTextarea = <T extends FieldValues>({
  name,
  control,
  label,
  placeholder,
  isRequired = false,
  labelWidth = '250px',
  column = false,
  inputWidth = '500px',
  size = 'sm',
  resize = 'vertical',
  customErrorMessage,
  onClear,
  labelVariant = 'h4',
  labelColor = 'textSecondary',
  height,
}: FormTextareaProps<T>) => (
  <Flex flexDirection={column ? 'column' : 'row'}>
    <Block
      width={column ? '100%' : (labelWidth as SizeProperty)}
      marginBottom={column ? '0.5rem' : undefined}
    >
      <Typography variant={labelVariant} color={labelColor}>
        <>
          {label}
          {isRequired && <span style={{ color: 'red' }}> *</span>}
        </>
      </Typography>
    </Block>
    <Controller
      name={name}
      control={control}
      rules={{
        required: isRequired,
      }}
      render={({ field, fieldState }) => {
        const formError = fieldState.error?.message;
        const customError = customErrorMessage;
        const hasError = formError || customError;

        // Show error if field has been touched and there's a validation error
        const shouldShowError = fieldState.isTouched && hasError;

        return (
          <Block width={column ? '100%' : (inputWidth as SizeProperty)}>
            <Textarea
              size={size}
              placeholder={placeholder}
              isInvalid={!!shouldShowError}
              height={height}
              resize={resize}
              {...field}
              onChange={(e) => {
                field.onChange(e);
                if (onClear && e.target.value === '') {
                  onClear();
                }
              }}
            />
            {shouldShowError && (
              <Typography variant="body2" color="error">
                {formError || customError}
              </Typography>
            )}
          </Block>
        );
      }}
    />
  </Flex>
);
